import { Button, ControlLink, Input, LoadingBalls, Stack, Text } from '@moda/om';
import React from 'react';
import { gql } from '@apollo/client';
import { hrefFor } from '../../routers';
import { SmsPumoModuleFragment } from '../../generated/types/SmsPumoModuleFragment';
import { RestrictPlatform } from '../RestrictPlatform';
import { PhoneNumberTermsAndConditions } from '../PhoneNumberTermsAndConditions';
import { useSmsPumoModule } from './useSmsPumoModule';
import {
  SmsPumoModuleSlides,
  SMS_PUMO_MODULE_SLIDES_FRAGMENT
} from './SmsPumoModuleSlides/SmsPumoModuleSlides';

import './SmsPumoModule.scss';

export const SMS_PUMO_MODULE_FRAGMENT = gql`
  fragment SmsPumoModuleFragment on SmsPumoModule {
    name
    headlineText: headline_text
    bodyText: body_text
    platforms: platform
    ...SmsPumoModuleSlidesFragment
  }
  ${SMS_PUMO_MODULE_SLIDES_FRAGMENT}
`;

interface Props {
  smsPumoModule: SmsPumoModuleFragment;
}

export const SmsPumoModule: React.FC<Props> = ({ smsPumoModule }) => {
  const {
    isSubscribed,
    handleSubmit,
    error,
    handleChangePhone,
    phone,
    handleChangeEmail,
    phoneNumberValidationError,
    mailValidationError,
    email,
    loading
  } = useSmsPumoModule();

  const { headlineText, bodyText, platforms } = smsPumoModule;

  return (
    <RestrictPlatform platforms={platforms}>
      <div className="SmsPumoModule">
        <div className="SmsPumoModule__slides">
          <SmsPumoModuleSlides smsPumoModule={smsPumoModule} />
        </div>

        <Stack className="SmsPumoModule__content" space={4}>
          {!isSubscribed && (
            <>
              <Text className="SmsPumoModule__title" family="serif" treatment="h3">
                {headlineText}
              </Text>

              <Stack className="SmsPumoModule__body-text" alignItems="center" space={2}>
                <Text treatment="h6">{bodyText}</Text>
              </Stack>

              <form className="SmsPumoModule__form" onSubmit={handleSubmit}>
                <Stack alignItems="center" className="SmsPumoModule__form-content" space={3}>
                  <Input
                    className="SmsPumoModule__input"
                    label="E-Mail **"
                    name="email"
                    disabled={isSubscribed}
                    placeholder="E-mail Address"
                    type="email"
                    error={mailValidationError}
                    required
                    value={email}
                    onChange={handleChangeEmail}
                  />

                  <Input
                    className="SmsPumoModule__input"
                    label="U.S. Mobile Number †"
                    name="phone"
                    error={phoneNumberValidationError}
                    disabled={isSubscribed}
                    placeholder="Mobile Number"
                    type="phone"
                    value={phone}
                    onChange={handleChangePhone}
                  />

                  <PhoneNumberTermsAndConditions />

                  <Text className="SmsPumoModule__email-legal" treatment="body2" color="cement">
                    ** Email required
                  </Text>

                  {error && <Text color="code-red">We are unable to process this request</Text>}

                  {loading && <LoadingBalls className="SmsPumoModule__loading" />}

                  <Button
                    className="SmsPumoModule__call-to-action"
                    disabled={loading || isSubscribed}
                    type="submit"
                    role="button"
                  >
                    SIGN UP
                  </Button>

                  <Stack alignItems="center" space={2}>
                    <Text treatment="body2" color="cement">
                      * Terms and Conditions Apply
                    </Text>

                    <Text treatment="body2" color="cement">
                      View{' '}
                      <ControlLink
                        to={hrefFor.TermsAndConditionsPage()}
                        className="SmsPumoModule__footer-link"
                        underlined
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <Text treatment="body2" color="cement">
                          Terms
                        </Text>
                      </ControlLink>{' '}
                      &amp;{' '}
                      <ControlLink
                        to={hrefFor.PrivacyPolicyPage()}
                        className="SmsPumoModule__footer-link"
                        underlined
                        rel="noopener noreferrer"
                        target="_blank"
                      >
                        <Text treatment="body2" color="cement">
                          Privacy Policy
                        </Text>
                      </ControlLink>
                    </Text>
                  </Stack>
                </Stack>
              </form>
            </>
          )}

          {isSubscribed && (
            <Stack space={4}>
              <Text className="SmsPumoModule__title" family="serif" treatment="h3">
                You're on the list.
              </Text>

              <Text className="SmsPumoModule__title" family="serif" treatment="h4">
                Thanks for signing up! Now check your messages - your{' '}
                <span className="SmsPumoModule__discount">10% off</span> is waiting for you.
              </Text>
            </Stack>
          )}
        </Stack>
      </div>
    </RestrictPlatform>
  );
};
